/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Main Application Shell Component
 *
 * Provides the overall layout structure with sidebar navigation and content area.
 * Uses Shoelace components for UI and integrates with shared Scion components.
 */

import { LitElement, html, css, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

// Import shared components
import './shared/nav.js';
import './shared/header.js';
import './shared/breadcrumb.js';
import type { User } from '../shared/types.js';
import type { AccessDeniedDetail } from '../client/api.js';
import { showAccessDeniedToast } from '../utils/access-denied.js';
import { performLogout } from '../utils/auth.js';
import { setDocumentTitle, PAGE_TITLE_EVENT } from '../client/page-title.js';
import type { PageTitleDetail } from '../client/page-title.js';
import { enterAppFrame, exitAppFrame } from './shared/app-frame.js';
import { isFeatureEnabled } from '../utils/feature-flags.js';
import { ARTIFACTS_FLAG } from '../client/artifacts.js';

/**
 * Page title configuration
 */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/projects': 'Projects',
  '/agents': 'Agents',
  '/terminals': 'Terminals',
  '/brokers': 'Brokers',
  '/settings': 'Settings',
  '/admin/scheduler': 'Scheduler',
  '/admin/users': 'Users',
  '/admin/groups': 'Groups',
  '/admin/server-config': 'Server Config',
  '/admin/federation': 'Federation',
  '/admin/integrations': 'Integrations',
  '/admin/roles': 'Roles',
  '/admin/role-bindings': 'Role Bindings',
  '/admin/quotas': 'Quotas',
  '/metrics': 'Metrics',
  '/admin/skill-registries': 'Skill Registries',
  '/skills': 'Skills',
  '/github-app/installed': 'GitHub App Setup',
  '/onboarding': 'Setup',
};

@customElement('scion-app')
export class ScionApp extends LitElement {
  /**
   * Current authenticated user
   */
  @property({ type: Object })
  user: User | null = null;

  /**
   * Current URL path for navigation highlighting
   */
  @property({ type: String })
  currentPath = '/';

  /**
   * Whether the mobile drawer is open
   */
  @state()
  _drawerOpen = false;

  @state()
  _sidebarCollapsed = false;

  /** Bound listener references for cleanup */
  private _accessDeniedHandler = this.handleAccessDenied.bind(this);
  private _pageTitleHandler = this.handlePageTitle.bind(this);

  static override styles = css`
    :host {
      display: flex;
      height: var(--scion-app-height, 100dvh);
      background: var(--scion-bg, #f8fafc);
      touch-action: manipulation;
    }

    /* Desktop sidebar */
    .sidebar {
      display: flex;
      flex-shrink: 0;
      position: sticky;
      top: 0;
      height: var(--scion-app-height, 100dvh);
      /* Landscape on a notched phone (the page uses viewport-fit=cover):
         the nav moves clear of the notch and the home indicator, and the
         sidebar paints the nav's surface under the gaps. 0 elsewhere. */
      box-sizing: border-box;
      padding-left: env(safe-area-inset-left, 0px);
      padding-bottom: env(safe-area-inset-bottom, 0px);
      background: var(--scion-surface, #ffffff);
    }

    /* The sidebar sits between the header and the left edge and takes the
       left inset itself, so the header does not repeat it. */
    scion-header {
      --scion-header-inset-left: 0px;
    }

    @media (max-width: 768px) {
      .sidebar {
        display: none;
      }
      scion-header {
        --scion-header-inset-left: env(safe-area-inset-left, 0px);
      }
    }

    /* Hide mobile drawer until Shoelace is loaded */
    /* This prevents SSR from rendering a visible duplicate nav */
    sl-drawer:not(:defined) {
      display: none;
    }

    /* Mobile drawer */
    /* The drawer grows by the left inset and pads it, so the nav keeps its
       width and clears the notch in landscape. 0 elsewhere. */
    .mobile-drawer {
      --size: calc(280px + env(safe-area-inset-left, 0px));
    }

    .mobile-drawer::part(panel) {
      background: var(--scion-surface, #ffffff);
      padding-left: env(safe-area-inset-left, 0px);
      box-sizing: border-box;
    }

    .mobile-drawer::part(close-button) {
      color: var(--scion-text, #1e293b);
    }

    .mobile-drawer::part(close-button):hover {
      color: var(--scion-primary, #3b82f6);
    }

    /* Main content area */
    .main {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0; /* Prevent flex overflow */
    }

    /* Content wrapper */
    .content {
      flex: 1;
      padding: 1.5rem;
      /* The last of the scrolled content clears the home indicator (the
         page uses viewport-fit=cover); the inset is 0 elsewhere. */
      padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 0px));
      /* Likewise clear of the notch and rounded corners in landscape. The
         right edge always meets the screen; the left only once the sidebar
         gives way to the drawer (below), as the sidebar takes the left
         inset itself. */
      padding-right: max(1.5rem, env(safe-area-inset-right, 0px));
      overflow: auto;
      overscroll-behavior: contain;
      display: flex;
      flex-direction: column;
    }

    @media (max-width: 768px) {
      .content {
        padding-left: max(1.5rem, env(safe-area-inset-left, 0px));
      }
    }

    @media (max-width: 640px) {
      .content {
        padding: 1rem;
        padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
        padding-inline: max(1rem, env(safe-area-inset-left, 0px))
          max(1rem, env(safe-area-inset-right, 0px));
      }
    }

    /* Max width container */
    .content-inner {
      max-width: var(--scion-content-max-width, 1400px);
      margin: 0 auto;
      width: 100%;
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    /* Loading overlay */
    .loading-overlay {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.8);
      z-index: 9999;
      opacity: 0;
      visibility: hidden;
      transition:
        opacity 0.2s ease,
        visibility 0.2s ease;
    }

    .loading-overlay.visible {
      opacity: 1;
      visibility: visible;
    }

    @media (prefers-color-scheme: dark) {
      .loading-overlay {
        background: rgba(15, 23, 42, 0.8);
      }
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    enterAppFrame();
    window.addEventListener('scion:access-denied', this._accessDeniedHandler as EventListener);
    this.addEventListener(PAGE_TITLE_EVENT, this._pageTitleHandler as EventListener);
    this.updateDocumentTitle();
    try {
      this._sidebarCollapsed = localStorage.getItem('scion-sidebar-collapsed') === 'true';
    } catch {
      // localStorage may be unavailable (SecurityError in restricted contexts)
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    exitAppFrame();
    window.removeEventListener('scion:access-denied', this._accessDeniedHandler as EventListener);
    this.removeEventListener(PAGE_TITLE_EVENT, this._pageTitleHandler as EventListener);
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('currentPath')) {
      this.updateDocumentTitle();
    }
  }

  /**
   * Handle page-title events from detail page components to refine the
   * document title with entity-specific context (project name, agent name, etc.).
   */
  private handlePageTitle(event: CustomEvent<PageTitleDetail>): void {
    const segments = event.detail?.segments;
    if (segments && segments.length > 0) {
      setDocumentTitle(...segments);
    }
  }

  /**
   * Set the document title from the current path-based page title.
   * Detail page components will refine this further via scion:page-title events.
   */
  private updateDocumentTitle(): void {
    const title = this.getPageTitle();
    setDocumentTitle(title);
  }

  private handleAccessDenied(event: CustomEvent<AccessDeniedDetail>): void {
    const detail = event.detail || {};
    // Mark as handled to prevent duplicate toasts if chat-shell is also mounted.
    (detail as Record<string, unknown>)._handled = true;
    showAccessDeniedToast(detail);
  }

  override render(): TemplateResult {
    const pageTitle = this.getPageTitle();

    return html`
      <!-- Desktop Sidebar -->
      <aside class="sidebar">
        <scion-nav
          .user=${this.user}
          .currentPath=${this.currentPath}
          ?collapsed=${this._sidebarCollapsed}
          @sidebar-toggle=${(): void => this.handleSidebarToggle()}
        ></scion-nav>
      </aside>

      <!-- Mobile Drawer -->
      <sl-drawer
        class="mobile-drawer"
        ?open=${this._drawerOpen}
        placement="start"
        @sl-hide=${(): void => this.handleDrawerClose()}
      >
        <scion-nav
          .user=${this.user}
          .currentPath=${this.currentPath}
          .hideCollapse=${true}
          @nav-click=${(): void => this.handleNavClick()}
        ></scion-nav>
      </sl-drawer>

      <!-- Main Content -->
      <main class="main">
        <scion-header
          .user=${this.user}
          .currentPath=${this.currentPath}
          .pageTitle=${pageTitle}
          ?showMobileMenu=${true}
          @mobile-menu-toggle=${(): void => this.handleMobileMenuToggle()}
          @logout=${(): void => this.handleLogout()}
        ></scion-header>

        <div class="content">
          <div class="content-inner">
            <slot></slot>
          </div>
        </div>
      </main>
    `;
  }

  /**
   * Get the page title based on current path
   */
  private getPageTitle(): string {
    // Check for exact match
    if (PAGE_TITLES[this.currentPath]) {
      return PAGE_TITLES[this.currentPath];
    }

    // Check for pattern matches
    if (this.currentPath === '/projects/new') {
      return 'Create Project';
    }
    if (this.currentPath.match(/^\/projects\/[^/]+\/settings$/)) {
      return 'Project Settings';
    }
    if (this.currentPath.match(/^\/projects\/[^/]+\/schedules$/)) {
      return 'Schedules';
    }
    if (this.currentPath.match(/^\/projects\/[^/]+\/metrics$/)) {
      return 'Project Metrics';
    }
    if (
      this.currentPath.match(/^\/projects\/[^/]+\/artifacts\/[^/]+$/) &&
      isFeatureEnabled(ARTIFACTS_FLAG)
    ) {
      return 'Artifact';
    }
    if (this.currentPath === '/artifacts' && isFeatureEnabled(ARTIFACTS_FLAG)) {
      return 'Artifacts';
    }
    if (this.currentPath.match(/^\/projects\/[^/]+\/templates\/[^/]+$/)) {
      return 'Template';
    }
    if (this.currentPath.startsWith('/projects/')) {
      return 'Project';
    }
    if (this.currentPath === '/agents/new') {
      return 'Create Agent';
    }
    if (this.currentPath.match(/^\/agents\/[^/]+\/terminal$/)) {
      return 'Terminal';
    }
    if (this.currentPath === '/terminals' || this.currentPath.match(/^\/terminals\/[^/]+$/)) {
      return 'Terminals';
    }
    if (this.currentPath.match(/^\/agents\/[^/]+\/configure$/)) {
      return 'Configure Agent';
    }
    if (this.currentPath.match(/^\/agents\/[^/]+\/edit$/)) {
      return 'Edit Agent';
    }
    if (this.currentPath.startsWith('/agents/')) {
      return 'Agent';
    }
    if (this.currentPath.startsWith('/brokers/')) {
      return 'Broker';
    }
    if (this.currentPath.match(/^\/settings\/harness-configs\/[^/]+$/)) {
      return 'Harness Config';
    }
    if (this.currentPath.match(/^\/settings\/templates\/[^/]+$/)) {
      return 'Template';
    }
    if (this.currentPath.match(/^\/admin\/groups\/[^/]+$/)) {
      return 'Group';
    }
    if (this.currentPath === '/admin/maintenance') {
      return 'Maintenance';
    }
    if (this.currentPath.match(/^\/admin\/integrations\/[^/]+$/)) {
      return 'Integration';
    }
    if (this.currentPath === '/skills/new') {
      return 'Create Skill';
    }
    if (this.currentPath.startsWith('/skills/')) {
      return 'Skill';
    }
    if (this.currentPath.match(/^\/admin\/skill-registries\/[^/]+$/)) {
      return 'Skill Registry';
    }

    return 'Page Not Found';
  }

  private handleSidebarToggle(): void {
    this._sidebarCollapsed = !this._sidebarCollapsed;
    try {
      localStorage.setItem('scion-sidebar-collapsed', String(this._sidebarCollapsed));
    } catch {
      // localStorage may be unavailable (SecurityError in restricted contexts)
    }
  }

  private handleMobileMenuToggle(): void {
    this._drawerOpen = !this._drawerOpen;
  }

  /**
   * Handle drawer close event
   */
  private handleDrawerClose(): void {
    this._drawerOpen = false;
  }

  /**
   * Handle navigation click from nav component
   */
  private handleNavClick(): void {
    // Close drawer on navigation in mobile
    this._drawerOpen = false;
  }

  /**
   * Handle logout action.
   * Delegates to shared performLogout() utility (design doc Section 4.1).
   */
  private handleLogout(): void {
    performLogout();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'scion-app': ScionApp;
  }
}
